import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Chat,
  Message,
  TrendingUp,
  Info
} from '@mui/icons-material';
import apiService from '../../services/apiService';

function ConversationStatsCard({ companyId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConversationStats();
  }, [companyId]);

  const fetchConversationStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.fetchCompanyConversationStats(companyId);

      if (result.success) {
        setStats(result.data);
      } else {
        console.error('Failed to fetch conversation stats:', result.error);
        setError(result.error);
      }
    } catch (error) {
      console.error('Error fetching conversation stats:', error);
      setError('Failed to load conversation statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const getAverageMessagesPerConversation = () => {
    if (!stats || stats.total_conversations === 0) return 0;
    return Math.round(stats.total_messages / stats.total_conversations);
  };

  if (loading) {
    return (
      <Card sx={{ width: '100%' }}>
        <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>
            📊 Conversation Statistics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              📊 Conversation Statistics
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>
            📊 Conversation Statistics
          </Typography>
          <Tooltip title="Statistics exclude ghost conversations and represent real user interactions" arrow>
            <IconButton size="small" sx={{ color: 'text.secondary' }}>
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Total Conversations */}
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Chat sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
                <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                  {formatNumber(stats?.total_conversations || 0)}
                </Typography>
                <Typography variant="body1" color="text.primary">
                  Total Conversations
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Total Messages */}
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Message sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
                <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                  {formatNumber(stats?.total_messages || 0)}
                </Typography>
                <Typography variant="body1" color="text.primary">
                  Total Messages
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Average Messages per Conversation */}
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <TrendingUp sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
                <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                  {getAverageMessagesPerConversation()}
                </Typography>
                <Typography variant="body1" color="text.primary">
                  Avg. Messages/Conversation
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>


      </CardContent>
    </Card>
  );
}

export default ConversationStatsCard; 